/**
 * Offer QA preview toolbar (`toolbar--preview.twig`).
 *
 * The selection lives in the query string (schema: OfferQaSelection). Every change
 * rebuilds the URL from `location.search`, keeping every param that is not ours
 * (token, x-craft-preview, x-craft-live-preview, toolbar, ...) byte for byte, and
 * reloads with `location.replace()`. Values equal to the defaults are left out
 * (absent = default); an empty list is kept as an empty param (`hotel=`).
 *
 * A copy of our params is stored as `{v: 1, params}` in localStorage, because
 * Craft's Preview pane rebuilds the iframe URL on every refresh and drops them;
 * the inline head script `_inline-js/offer-preview-restore.js` puts them back.
 * The panel's open state, the page scroll and the focused control survive the
 * reload through sessionStorage.
 *
 * "Generate PDF" (preview requests only, where the page renders a grant) posts the
 * grant and our current params to `teamleader-focus/offer-qa/generate`, which queues
 * the QA PDF job, then polls `offer-qa/status` every 2 seconds until the PDF is done
 * or failed, giving up after 5 minutes. The running request is kept in
 * sessionStorage (`{storageKey}:pdf`), so a toolbar-driven reload picks it up again.
 * The preview token and the `x-craft-*` params never go into these requests.
 */
import type { AlpineComponent } from '~/js/offer-generator/alpine'

export type ListKey = 'teambuildings' | 'locations' | 'caterings' | 'hotels'
export type FlagKey = 'timetable' | 'extras'
export type StateKey = ListKey | FlagKey | 'participants'

export interface ToolbarState {
    teambuildings: number[]
    locations: number[]
    caterings: number[]
    hotels: number[]
    participants: number
    timetable: boolean
    extras: boolean
}

export interface ToolbarOption {
    id: number
    title: string
    live?: boolean
}

export type PdfStatus = 'idle' | 'queued' | 'running' | 'done' | 'failed'

export interface PdfConfig {
    grant: string
    generateUrl: string
    statusUrl: string
    labels: Record<'queued' | 'running' | 'done' | 'failed' | 'busy' | 'timeout' | 'network', string>
}

export interface PdfState {
    status: PdfStatus
    progress: number
    requestId?: string
    url?: string
    error?: string
    startedAt?: number
}

interface PdfStatusResponse {
    status?: Exclude<PdfStatus, 'idle'>
    progress?: number
    url?: string
    error?: string
}

export interface ToolbarConfig {
    storageKey: string
    entryId: number
    siteId: number
    /** Query param name per state key, rendered from the OfferQaSelection constants */
    params: Record<StateKey, string>
    caps: Record<ListKey, number> & { participantsMin: number, participantsMax: number }
    selection: ToolbarState
    defaults: ToolbarState
    options: Record<ListKey, ToolbarOption[]>
    /** Present in preview requests only */
    pdf: PdfConfig | null
}

interface SavedState {
    v: number
    params: Record<string, string>
}

interface UiState {
    open?: boolean
    scrollY?: number
    focusId?: string
}

export const STORAGE_VERSION = 1

const LIST_KEYS: ListKey[] = ['teambuildings', 'locations', 'caterings', 'hotels']
const FLAG_KEYS: FlagKey[] = ['timetable', 'extras']
const DEBOUNCE_MS = 500
const PDF_POLL_MS = 2000
const PDF_TIMEOUT_MS = 5 * 60 * 1000

const sameList = (a: number[], b: number[]): boolean =>
    a.length === b.length && a.every((value, index) => value === b[index])

/**
 * Returns a query pair's param name, decoded, without a trailing `[]`.
 */
const paramName = (pair: string): string => {
    const raw = pair.split('=')[0]

    try {
        return decodeURIComponent(raw.replace(/\+/g, ' ')).replace(/\[.*\]$/, '')
    } catch {
        return raw
    }
}

/**
 * Encodes a state as our query params, leaving out values equal to the defaults.
 */
export const encodeState = (
    state: ToolbarState,
    defaults: ToolbarState,
    params: Record<StateKey, string>,
): Record<string, string> => {
    const encoded: Record<string, string> = {}

    for (const key of LIST_KEYS) {
        if (!sameList(state[key], defaults[key])) {
            encoded[params[key]] = state[key].join(',')
        }
    }

    if (state.participants !== defaults.participants) {
        encoded[params.participants] = String(state.participants)
    }

    for (const key of FLAG_KEYS) {
        if (state[key] !== defaults[key]) {
            encoded[params[key]] = state[key] ? '1' : '0'
        }
    }

    return encoded
}

/**
 * Whether a query string carries any of our params.
 */
export const hasOwnParams = (search: string, names: string[]): boolean =>
    search.replace(/^\?/, '').split('&').filter(Boolean).some(pair => names.includes(paramName(pair)))

/**
 * Builds a query string: every foreign pair kept as is, ours replaced by `ours`.
 * Commas stay readable (`tb=1,2`).
 */
export const buildSearch = (search: string, ours: Record<string, string>, names: string[]): string => {
    const kept = search.replace(/^\?/, '').split('&').filter(pair => pair !== '' && !names.includes(paramName(pair)))
    const added = Object.entries(ours).map(
        ([name, value]) => `${encodeURIComponent(name)}=${encodeURIComponent(value).replace(/%2C/gi, ',')}`,
    )
    const pairs = [...kept, ...added]

    return pairs.length > 0 ? `?${pairs.join('&')}` : ''
}

/**
 * Returns a URL without the preview token and the `x-craft-*` params, which Craft
 * adds to every URL it renders in a preview request.
 */
export const withoutPreviewParams = (url: string): string => {
    const parsed = new URL(url, window.location.href)

    for (const name of [...parsed.searchParams.keys()]) {
        if (name === 'token' || name.startsWith('x-craft-')) {
            parsed.searchParams.delete(name)
        }
    }

    return parsed.toString()
}

/**
 * Returns our params from a query string, as sent with the PDF request.
 */
export const ownParams = (search: string, names: string[]): Array<[string, string]> =>
    [...new URLSearchParams(search)].filter(([name]) => names.includes(name.replace(/\[.*\]$/, '')))

/**
 * Returns the status line of a PDF request.
 */
const pdfStatusLine = (pdf: PdfState, labels: PdfConfig['labels']): string => {
    if (pdf.status === 'running' && pdf.progress > 0) {
        return `${labels.running} ${pdf.progress}%`
    }

    if (pdf.status === 'failed') {
        return labels.failed.replace('{error}', pdf.error ?? '')
    }

    return pdf.status === 'idle' ? '' : labels[pdf.status]
}

const cloneState = (state: ToolbarState): ToolbarState => ({
    ...state,
    teambuildings: [...state.teambuildings],
    locations: [...state.locations],
    caterings: [...state.caterings],
    hotels: [...state.hotels],
})

const readJson = <T>(storage: Storage, key: string): T | null => {
    try {
        return JSON.parse(storage.getItem(key) ?? 'null') as T | null
    } catch {
        return null
    }
}

const writeJson = (storage: Storage, key: string, value: unknown): void => {
    try {
        storage.setItem(key, JSON.stringify(value))
    } catch {
        // Storage full or blocked: the URL still carries the state.
    }
}

const removeItem = (storage: Storage, key: string): void => {
    try {
        storage.removeItem(key)
    } catch {
        // Storage blocked: nothing to clear.
    }
}

const sessionStore = (): Storage | null => {
    try {
        return window.sessionStorage
    } catch {
        return null
    }
}

const localStore = (): Storage | null => {
    try {
        return window.localStorage
    } catch {
        return null
    }
}

interface ToolbarData {
    open: boolean
    pending: boolean
    state: ToolbarState
    timer: number | undefined
    pdf: PdfState
    pdfTimer: number | undefined
    readonly names: string[]
    readonly uiKey: string
    readonly pdfKey: string
    readonly pdfBusy: boolean
    readonly pdfLabel: string
    show(): void
    hide(): void
    setList(name: string, values: number[]): void
    setFlag(name: string, checked: boolean): void
    queueParticipants(value: string): void
    commitParticipants(value: string): void
    reset(): void
    apply(): void
    persist(ours: Record<string, string>): void
    navigate(ours: Record<string, string>): void
    saveUi(): void
    generatePdf(): Promise<void>
    pollPdf(): Promise<void>
    setPdf(pdf: PdfState): void
}

export default (config: ToolbarConfig): AlpineComponent<ToolbarData> => ({
    open: false,
    pending: false,
    state: cloneState(config.selection),
    timer: undefined,
    pdf: { status: 'idle', progress: 0 },
    pdfTimer: undefined,

    get names(): string[] {
        return Object.values(config.params)
    },

    get uiKey(): string {
        return `${config.storageKey}:ui`
    },

    get pdfKey(): string {
        return `${config.storageKey}:pdf`
    },

    get pdfBusy(): boolean {
        return this.pdf.status === 'queued' || this.pdf.status === 'running'
    },

    get pdfLabel(): string {
        const labels = config.pdf?.labels

        if (!labels) {
            return ''
        }

        return pdfStatusLine(this.pdf, labels)
    },

    init(): void {
        const session = sessionStore()
        const ui = session ? readJson<UiState>(session, this.uiKey) : null

        if (session) {
            removeItem(session, this.uiKey)
        }

        this.open = ui?.open ?? false

        if (ui) {
            this.$nextTick(() => {
                if (typeof ui.scrollY === 'number') {
                    window.scrollTo({ top: ui.scrollY, behavior: 'instant' })
                }

                if (ui.focusId) {
                    document.getElementById(ui.focusId)?.focus({ preventScroll: true })
                }
            })
        }

        // A URL with our params (shared link, restored state) is what the page shows: keep storage in step.
        if (hasOwnParams(window.location.search, this.names)) {
            this.persist(encodeState(this.state, config.defaults, config.params))
        }

        // A PDF requested before a reload: show its result, or keep polling it.
        const savedPdf = config.pdf && session ? readJson<PdfState>(session, this.pdfKey) : null

        if (savedPdf?.status && savedPdf.status !== 'idle') {
            this.pdf = savedPdf

            if (this.pdfBusy) {
                void this.pollPdf()
            }
        }
    },

    show(): void {
        this.open = true
        this.$nextTick(() => this.$refs.close?.focus())
    },

    hide(): void {
        this.open = false
        this.$nextTick(() => this.$refs.pill?.focus())
    },

    setList(name: string, values: number[]): void {
        if (!LIST_KEYS.includes(name as ListKey)) {
            return
        }

        const key = name as ListKey
        this.state[key] = values.map(Number).filter(Number.isInteger).slice(0, config.caps[key])
        this.apply()
    },

    setFlag(name: string, checked: boolean): void {
        if (!FLAG_KEYS.includes(name as FlagKey)) {
            return
        }

        this.state[name as FlagKey] = checked
        this.apply()
    },

    queueParticipants(value: string): void {
        window.clearTimeout(this.timer)
        this.timer = window.setTimeout(() => this.commitParticipants(value), DEBOUNCE_MS)
    },

    commitParticipants(value: string): void {
        window.clearTimeout(this.timer)

        const participants = Number(value)

        if (
            value.trim() === ''
            || !Number.isInteger(participants)
            || participants < config.caps.participantsMin
            || participants > config.caps.participantsMax
            || participants === this.state.participants
        ) {
            return
        }

        this.state.participants = participants
        this.apply()
    },

    reset(): void {
        const local = localStore()

        if (local) {
            removeItem(local, config.storageKey)
        }

        this.state = cloneState(config.defaults)
        this.navigate({})
    },

    apply(): void {
        const ours = encodeState(this.state, config.defaults, config.params)
        this.persist(ours)
        this.navigate(ours)
    },

    persist(ours: Record<string, string>): void {
        const local = localStore()

        if (local) {
            const saved: SavedState = { v: STORAGE_VERSION, params: ours }
            writeJson(local, config.storageKey, saved)
        }
    },

    navigate(ours: Record<string, string>): void {
        const { pathname, search, hash } = window.location
        const next = buildSearch(search, ours, this.names)

        if (next === search) {
            return
        }

        this.pending = true
        this.saveUi()
        window.location.replace(`${pathname}${next}${hash}`)
    },

    saveUi(): void {
        const session = sessionStore()

        if (!session) {
            return
        }

        const active = document.activeElement
        const focusId = active instanceof HTMLElement && active.id && this.$root.contains(active) ? active.id : undefined
        const ui: UiState = { open: this.open, scrollY: window.scrollY, focusId }

        writeJson(session, this.uiKey, ui)
    },

    async generatePdf(): Promise<void> {
        if (!config.pdf || this.pdfBusy) {
            return
        }

        const body = new FormData()
        body.append('grant', config.pdf.grant)

        for (const [name, value] of ownParams(window.location.search, this.names)) {
            body.append(name, value)
        }

        this.setPdf({ status: 'queued', progress: 0, startedAt: Date.now() })

        try {
            const response = await fetch(withoutPreviewParams(config.pdf.generateUrl), {
                method: 'POST',
                body,
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            })
            const data = await response.json() as { requestId?: string, error?: string }

            if (!response.ok || !data.requestId) {
                this.setPdf({
                    status: 'failed',
                    progress: 0,
                    error: response.status === 429 ? config.pdf.labels.busy : (data.error ?? config.pdf.labels.network),
                })
                return
            }

            this.setPdf({ ...this.pdf, requestId: data.requestId })
            void this.pollPdf()
        } catch {
            this.setPdf({ status: 'failed', progress: 0, error: config.pdf.labels.network })
        }
    },

    async pollPdf(): Promise<void> {
        window.clearTimeout(this.pdfTimer)

        if (!config.pdf || !this.pdf.requestId || !this.pdfBusy) {
            return
        }

        if (Date.now() - (this.pdf.startedAt ?? Date.now()) > PDF_TIMEOUT_MS) {
            this.setPdf({ ...this.pdf, status: 'failed', error: config.pdf.labels.timeout })
            return
        }

        try {
            const url = new URL(withoutPreviewParams(config.pdf.statusUrl))
            url.searchParams.set('requestId', this.pdf.requestId)
            url.searchParams.set('grant', config.pdf.grant)

            const response = await fetch(url, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                cache: 'no-store',
            })
            const data = await response.json() as PdfStatusResponse

            if (!response.ok || !data.status) {
                this.setPdf({ ...this.pdf, status: 'failed', error: data.error ?? config.pdf.labels.network })
                return
            }

            this.setPdf({
                ...this.pdf,
                status: data.status,
                progress: data.progress ?? 0,
                url: data.url,
                error: data.error,
            })
        } catch {
            // A dropped poll is retried until the timeout.
        }

        if (this.pdfBusy) {
            this.pdfTimer = window.setTimeout(() => void this.pollPdf(), PDF_POLL_MS)
        }
    },

    setPdf(pdf: PdfState): void {
        this.pdf = pdf

        const session = sessionStore()

        if (session) {
            writeJson(session, this.pdfKey, pdf)
        }
    },
})
